import React, { useEffect, useState, memo, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { GlassWaterIcon, BriefcaseIcon, UtensilsIcon, HomeIcon, GiftIcon, ChevronRightIcon, MusicIcon, PartyPopperIcon } from 'lucide-react';
export function CustomLabelDesignPage() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Form State
  const [selectedWine, setSelectedWine] = useState<string | null>(null);
  const [eventType, setEventType] = useState<string>('');
  const [customEventType, setCustomEventType] = useState<string>('');
  const [eventDate, setEventDate] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(12);
  const [designPref, setDesignPref] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [templatePreference, setTemplatePreference] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [timeline, setTimeline] = useState<string>('Standard (4-6 weeks)');
  const wines = [{
    name: 'Saumur Champigny',
    variety: 'Cabernet Franc',
    price: '$21',
    image: 'https://cdn.magicpatterns.com/uploads/jjQYV3XY2yBUSWDCGJJ5m5/Screenshot_2026-03-23_at_8.45.35_PM.png',
    badge: 'Signature Red'
  }, {
    name: 'Saumur Blanc',
    variety: 'Chenin Blanc',
    price: '$22',
    image: 'https://cdn.magicpatterns.com/uploads/8DxNiTzRKHiF4u4n4eaC5L/Screenshot_2026-03-23_at_9.09.11_PM.png',
    badge: 'Signature White',
    imageScale: 'scale-[1.3]'
  }, {
    name: 'Les Hirondelles',
    variety: 'Rosé',
    price: '$19',
    image: 'https://cdn.magicpatterns.com/uploads/cTccvparp2Z6bncj1faiiB/Screenshot_2026-03-23_at_8.56.30_PM.png',
    badge: 'Rosé'
  }, {
    name: 'Blanc de Noir',
    variety: 'Sparkling',
    price: '$25',
    image: 'https://cdn.magicpatterns.com/uploads/gevVDRX6R6AEyKTmRbKtjb/Screenshot_2026-03-23_at_9.07.43_PM.png',
    badge: 'Sparkling'
  }];
  const occasions = [{
    title: 'Weddings & Anniversaries',
    icon: <GlassWaterIcon className="w-6 h-6 text-wine" data-id="element-327" />,
    description: 'Celebrate your special day with a personalized vintage that guests will cherish forever.'
  }, {
    title: 'Corporate Events',
    icon: <BriefcaseIcon className="w-6 h-6 text-wine" data-id="element-328" />,
    description: 'Elevate your brand with custom-labeled bottles for clients, partners, and company milestones.'
  }, {
    title: 'Restaurants & Hospitality',
    icon: <UtensilsIcon className="w-6 h-6 text-wine" data-id="element-329" />,
    description: 'Offer an exclusive house wine that perfectly complements your menu and ambiance.'
  }, {
    title: 'Music Festivals',
    icon: <MusicIcon className="w-6 h-6 text-wine" data-id="element-330" />,
    description: 'Create memorable festival merchandise with custom-labeled bottles that attendees will treasure.'
  }, {
    title: 'College Parties',
    icon: <PartyPopperIcon className="w-6 h-6 text-wine" data-id="element-331" />,
    description: 'Celebrate graduations, formals, and Greek life events with personalized bottles for your crew.'
  }, {
    title: 'Personal Collections',
    icon: <GiftIcon className="w-6 h-6 text-wine" data-id="element-332" />,
    description: 'Create a signature wine for your private cellar or as a unique gift for loved ones.'
  }];
  const steps = [{
    title: 'Choose Your Wine',
    description: 'Select from our curated Loire Valley collection.'
  }, {
    title: 'Share Your Vision & Design',
    description: 'Tell us about your occasion, timeline, and design preferences. Note: U.S. shipments require a legal back label. Your label will only be at the front of the bottle.'
  }, {
    title: 'We Design',
    description: 'Our team will connect with you, put your vision to life, work some wine magic, to tailor the experience to you.'
  }, {
    title: 'You Receive',
    description: 'Beautifully labeled bottles delivered to your event. Ready to tell your story.'
  }];
  const galleryImages = [{
    src: 'https://cdn.magicpatterns.com/uploads/mbvGpJ4WXXyta7tztM1svG/Caprices_Festival_example.jpg',
    caption: 'Caprices Festival 2024',
    description: 'Custom labels created for the prestigious Caprices Music Festival in Gstaad, Switzerland.'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/6Vof2mCQcqoBZL4nCTFAwW/EliaKuhnPhotographe2021AnniversaireLolapartie1BD-200.jpg',
    caption: 'Birthday Party',
    description: 'Personalized bottles featuring a custom photo label for an intimate birthday celebration.'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/bUtnCwR2sPz7j1YUKwqjaR/graduation_party_example.jpg',
    caption: 'Graduation Party',
    description: 'A graduation celebration featuring custom-labeled bottles from Chateau de Chaintres in the Loire Valley.'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/wztTZfnX9tGvA384tbLFbU/fraternity_label_example.jpg',
    caption: 'College Party',
    description: 'Bespoke fraternity wine with a custom label from Chateau de Chaintres for a college organization.'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/crkmGwoGx1uoFqZdXmjkz2/3448c6e8-6125-4d30-b636-e8c6b32b3f34.jpg',
    caption: 'Corporate Events',
    description: 'Custom-labeled bottles for a luxury French shoe brand corporate event and an internationally recognized nonprofit Art Center.'
  }];
  return <main className="min-h-screen bg-cream pt-20" data-id="element-333">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden" data-id="element-334">
        <div className="absolute inset-0 z-0" style={{
        backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/wm9JygMQ5m6cT2LGSwwYjj/image.png")',
        backgroundPosition: 'center',
        backgroundSize: 'cover'
      }} data-id="element-335">
          <div className="absolute inset-0 bg-charcoal/60 mix-blend-multiply" data-id="element-336" />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-transparent to-charcoal/80" data-id="element-337" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center" data-id="element-338">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-339">
            <span className="text-gold font-semibold tracking-widest uppercase text-sm mb-6 block" data-id="element-340">
              Bespoke Experience
            </span>
            <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl text-white font-bold leading-tight mb-6" data-id="element-341">
              The <br className="hidden md:block" data-id="element-342" />
              <span className="italic text-gold" data-id="element-343">Experience</span>
            </h1>
            <p className="text-lg md:text-xl text-cream-100 mb-10 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-344">
              Transform exceptional Loire Valley wines into personalized
              keepsakes. A great wine deserves a presentation that matches its
              quality.
            </p>
            <a href="#choose-wine" className="inline-flex items-center px-8 py-4 bg-wine text-white rounded-sm font-medium hover:bg-wine-700 transition-all duration-300 shadow-lg" data-id="element-345">
              Start Your Design
            </a>
          </motion.div>
        </div>
      </section>

      {/* The Process Section (How It Works) */}
      <section id="process" className="py-20 bg-white" data-id="element-346">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-347">
          <div className="text-center mb-16" data-id="element-348">
            <span className="text-wine font-semibold tracking-wider uppercase text-xs mb-3 block" data-id="element-349">
              How It Works
            </span>
            <h2 className="font-playfair text-3xl md:text-4xl text-charcoal font-bold" data-id="element-350">
              Simple{' '}
              <span className="italic text-wine font-normal" data-id="element-351">Process</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4" data-id="element-352">
            {steps.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="relative" data-id="element-353">
                <div className="text-center" data-id="element-354">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-wine/10 mb-4" data-id="element-355">
                    <span className="font-playfair text-xl font-bold text-wine" data-id="element-356">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="font-playfair text-lg font-bold text-charcoal mb-2 leading-tight" data-id="element-357">
                    {step.title}
                  </h3>
                  <p className="text-brown/60 text-sm leading-relaxed" data-id="element-358">
                    {step.description}
                  </p>
                </div>
                {index < steps.length - 1 && <div className="hidden md:block absolute top-6 left-[calc(50%+24px)] w-[calc(100%-48px)] h-px bg-cream-200" data-id="element-359"></div>}
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Choose Your Wine Section (Step 1) */}
      <section id="choose-wine" className="py-24 bg-cream-50" data-id="element-360">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-361">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-362">
            <span className="text-gold font-semibold tracking-widest uppercase text-sm mb-3 block" data-id="element-363">
              Step 1
            </span>
            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal font-bold mb-6" data-id="element-364">
              Choose Your{' '}
              <span className="italic text-wine font-normal" data-id="element-365">Canvas</span>
            </h2>
            <p className="text-lg text-brown/70" data-id="element-366">
              Select from our curated collection of exceptional wines from
              Chateau de Chaintres.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-367">
            {wines.map((wine, index) => {
            const isSelected = selectedWine === wine.name;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} onClick={() => setSelectedWine(wine.name)} className={`bg-white rounded-sm overflow-hidden shadow-subtle border flex flex-col h-full transition-all duration-300 cursor-pointer ${isSelected ? 'border-gold ring-2 ring-gold/50 scale-[1.02]' : 'border-cream-200 hover:border-gold/50'}`} data-id="element-368">
                  <div className="relative h-72 bg-gray-100 p-6 flex items-center justify-center" data-id="element-369">
                    {wine.badge && <div className="absolute top-4 left-4 bg-wine text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-sm z-10" data-id="element-370">
                        {wine.badge}
                      </div>}
                    {isSelected && <div className="absolute top-4 right-4 bg-gold text-charcoal text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-sm z-10 shadow-md" data-id="element-371">
                        Selected
                      </div>}
                    <img src={wine.image} alt={wine.name} className={`h-full w-auto object-contain relative z-[1] ${wine.imageScale || ''}`} data-id="element-372" />
                  </div>
                  <div className="p-6 flex flex-col flex-grow text-center" data-id="element-373">
                    <div className="text-xs text-wine/70 font-semibold uppercase tracking-wider mb-2" data-id="element-374">
                      {wine.variety}
                    </div>
                    <h3 className="font-playfair text-xl font-bold text-charcoal mb-4 flex-grow" data-id="element-375">
                      {wine.name}
                    </h3>
                    <div className="text-lg font-bold text-charcoal mb-4" data-id="element-376">
                      {wine.price}
                    </div>

                    <button onClick={e => {
                  e.stopPropagation();
                  setSelectedWine(wine.name);
                  // Smooth scroll to step 2
                  document.getElementById('step-2')?.scrollIntoView({
                    behavior: 'smooth'
                  });
                }} className={`w-full py-3 border font-medium rounded-sm transition-colors duration-300 ${isSelected ? 'bg-gold border-gold text-charcoal' : 'border-wine text-wine hover:bg-wine hover:text-white'}`} data-id="element-377">
                      {isSelected ? 'Selected' : 'Select This Wine'}
                    </button>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Share Your Vision & Design Section (Step 2) */}
      <section id="step-2" className="py-24 bg-white border-t border-cream-200" data-id="element-378">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-379">
          <div className="text-center mb-16" data-id="element-380">
            <span className="text-gold font-semibold tracking-widest uppercase text-sm mb-3 block" data-id="element-381">
              Step 2
            </span>
            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal font-bold mb-6" data-id="element-382">
              Share Your{' '}
              <span className="italic text-wine font-normal" data-id="element-383">
                Vision & Design
              </span>
            </h2>
            <p className="text-lg text-brown/70" data-id="element-384">
              Tell us about your occasion, timeline, and design preferences.
            </p>
          </div>

          <div className={`transition-opacity duration-500 ${!selectedWine ? 'opacity-50 pointer-events-none' : 'opacity-100'}`} data-id="element-385">
            {!selectedWine && <div className="bg-cream-100 text-wine p-4 rounded-sm text-center mb-8 font-medium" data-id="element-386">
                Please select a wine in Step 1 to continue.
              </div>}

            <form className="space-y-10" data-id="element-387">
              {/* Event Type */}
              <div data-id="element-388">
                <label className="block text-charcoal font-bold mb-4" data-id="element-389">
                  Event Type *
                </label>
                <div className="flex flex-wrap gap-3" data-id="element-390">
                  {['Wedding', 'Corporate Event', 'Birthday', 'Anniversary', 'Bar Mitzvah', 'College Party', 'Other'].map(type => <button key={type} type="button" onClick={() => setEventType(type)} className={`px-6 py-3 rounded-sm border text-sm font-medium transition-colors ${eventType === type ? 'bg-wine border-wine text-white' : 'bg-white border-cream-200 text-charcoal hover:border-wine/50'}`} data-id="element-391">
                      {type}
                    </button>)}
                </div>
                {eventType === 'Other' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} transition={{
                duration: 0.3
              }} className="mt-4" data-id="element-392">
                    <input type="text" value={customEventType} onChange={e => setCustomEventType(e.target.value)} placeholder="Please specify your event type..." className="w-full px-4 py-3 rounded-sm border border-cream-200 focus:border-wine focus:ring-1 focus:ring-wine outline-none transition-colors bg-white" data-id="element-393" />
                  </motion.div>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-394">
                {/* Event Date */}
                <div data-id="element-395">
                  <label className="block text-charcoal font-bold mb-4" data-id="element-396">
                    Event Date
                  </label>
                  <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} className="w-full px-4 py-3 rounded-sm border border-cream-200 focus:border-wine focus:ring-1 focus:ring-wine outline-none transition-colors bg-white" data-id="element-397" />
                </div>

                {/* Timeline */}
                <div data-id="element-398">
                  <label className="block text-charcoal font-bold mb-4" data-id="element-399">
                    Timeline *
                  </label>
                  <select value={timeline} onChange={e => setTimeline(e.target.value)} className="w-full px-4 py-3 rounded-sm border border-cream-200 focus:border-wine focus:ring-1 focus:ring-wine outline-none transition-colors bg-white appearance-none" data-id="element-400">
                    <option value="Rush (2-3 weeks)" data-id="element-401">Rush (2-3 weeks)</option>
                    <option value="Standard (4-6 weeks)" data-id="element-402">
                      Standard (4-6 weeks)
                    </option>
                    <option value="Flexible" data-id="element-403">Flexible</option>
                  </select>
                </div>
              </div>

              {/* Quantity */}
              <div data-id="element-404">
                <label className="block text-charcoal font-bold mb-4" data-id="element-405">
                  Quantity *{' '}
                  <span className="text-sm font-normal text-brown/60" data-id="element-406">
                    (Minimum 12 bottles)
                  </span>
                </label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-6" data-id="element-407">
                  <div className="flex items-center border border-cream-200 rounded-sm bg-white w-fit" data-id="element-408">
                    <button type="button" onClick={() => setQuantity(Math.max(12, quantity - 1))} className="px-4 py-3 text-charcoal hover:bg-cream-50 transition-colors" data-id="element-409">
                      -
                    </button>
                    <input type="number" min="12" value={quantity} onChange={e => setQuantity(Math.max(12, parseInt(e.target.value) || 12))} className="w-20 text-center py-3 border-x border-cream-200 focus:outline-none appearance-none" data-id="element-410" />
                    <button type="button" onClick={() => setQuantity(quantity + 1)} className="px-4 py-3 text-charcoal hover:bg-cream-50 transition-colors" data-id="element-411">
                      +
                    </button>
                  </div>
                  {selectedWine && <div className="flex flex-col gap-2" data-id="element-412">
                      <div className="text-lg font-playfair text-wine bg-cream-50 px-6 py-3 rounded-sm border border-cream-200" data-id="element-413">
                        Estimated Total:{' '}
                        <span className="font-bold" data-id="element-414">
                          $
                          {(quantity * parseFloat(wines.find(w => w.name === selectedWine)?.price.replace('$', '') || '0')).toFixed(2)}
                        </span>
                        <span className="text-gold ml-1" data-id="element-415">*</span>
                      </div>
                      <p className="text-xs text-brown/60 italic" data-id="element-416">
                        <span className="text-gold" data-id="element-417">*</span> This is only an
                        estimate and does not include the design surcharge
                      </p>
                    </div>}
                </div>
              </div>

              {/* Design Preferences */}
              <div data-id="element-418">
                <label className="block text-charcoal font-bold mb-2" data-id="element-419">
                  Design Preferences *
                </label>
                <p className="text-sm text-red-600 italic font-medium mb-4" data-id="element-420">
                  Note: U.S. shipments require a legal back label. Your label
                  will only be at the front of the bottle.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-421">
                  {[{
                  id: 'upload',
                  title: 'Upload Design',
                  desc: 'I have my own artwork ready',
                  note: 'Accepted file types: jpg, png. Max. file size: 10 MB'
                }, {
                  id: 'team',
                  title: 'Custom Design',
                  desc: 'Work with our design team'
                }, {
                  id: 'template',
                  title: 'Use Template',
                  desc: 'Customize an existing design',
                  link: '#gallery'
                }].map(pref => <div key={pref.id} onClick={() => {
                  setDesignPref(pref.id);
                  if (pref.id === 'template' && pref.link) {
                    setTimeout(() => {
                      document.getElementById('gallery')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    }, 100);
                  }
                }} className={`p-6 rounded-sm border cursor-pointer transition-all ${designPref === pref.id ? 'border-wine ring-1 ring-wine bg-wine/5' : 'border-cream-200 bg-white hover:border-wine/50'}`} data-id="element-422">
                      <div className="flex items-center justify-between mb-2" data-id="element-423">
                        <h4 className="font-bold text-charcoal" data-id="element-424">
                          {pref.title}
                        </h4>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${designPref === pref.id ? 'border-wine' : 'border-gray-300'}`} data-id="element-425">
                          {designPref === pref.id && <div className="w-3 h-3 rounded-full bg-wine" data-id="element-426"></div>}
                        </div>
                      </div>
                      <p className="text-sm text-brown/70" data-id="element-427">{pref.desc}</p>
                      {pref.note && <p className="text-xs text-brown/50 mt-2 italic" data-id="element-428">
                          {pref.note}
                        </p>}
                      {pref.link && <p className="text-xs text-wine mt-2 font-medium" data-id="element-429">
                          View templates →
                        </p>}
                    </div>)}
                </div>

                {/* File Upload for Upload Design */}
                {designPref === 'upload' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} transition={{
                duration: 0.3
              }} className="mt-6" data-id="element-430">
                    <label className="block" data-id="element-431">
                      <input type="file" accept=".jpg,.jpeg,.png" onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const maxSize = 10 * 1024 * 1024; // 10 MB in bytes
                      if (file.size > maxSize) {
                        alert('File size exceeds 10 MB. Please upload a smaller file.');
                        e.target.value = '';
                        return;
                      }
                      setUploadedFile(file);
                    }
                  }} className="hidden" id="design-upload" data-id="element-432" />
                      <div className="border-2 border-dashed border-cream-200 rounded-sm p-8 text-center hover:border-wine/50 transition-colors cursor-pointer bg-cream-50" data-id="element-433">
                        {uploadedFile ? <div className="space-y-2" data-id="element-434">
                            <div className="text-wine font-medium" data-id="element-435">
                              {uploadedFile.name}
                            </div>
                            <div className="text-sm text-brown/60" data-id="element-436">
                              {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                            <button type="button" onClick={e => {
                        e.preventDefault();
                        setUploadedFile(null);
                        const input = document.getElementById('design-upload') as HTMLInputElement;
                        if (input) input.value = '';
                      }} className="text-sm text-wine hover:underline" data-id="element-437">
                              Remove file
                            </button>
                          </div> : <div className="space-y-2" data-id="element-438">
                            <div className="text-charcoal font-medium" data-id="element-439">
                              Click to upload your design
                            </div>
                            <div className="text-sm text-brown/60" data-id="element-440">
                              Accepted file types: jpg, png
                            </div>
                            <div className="text-xs text-brown/50" data-id="element-441">
                              Max. file size: 10 MB
                            </div>
                          </div>}
                      </div>
                    </label>
                  </motion.div>}

                {/* Template Preference Input */}
                {designPref === 'template' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} transition={{
                duration: 0.3
              }} className="mt-6" data-id="element-442">
                    <label className="block text-charcoal font-medium mb-2" data-id="element-443">
                      Which event template would you like?
                    </label>
                    <input type="text" value={templatePreference} onChange={e => setTemplatePreference(e.target.value)} placeholder="E.g., The Gentlemen of Pine, Caprices Festival, Graduation Party..." className="w-full px-4 py-3 rounded-sm border border-cream-200 focus:border-wine focus:ring-1 focus:ring-wine outline-none transition-colors bg-white" data-id="element-444" />
                  </motion.div>}
              </div>

              {/* Custom Message */}
              <div data-id="element-445">
                <label className="block text-charcoal font-bold mb-2" data-id="element-446">
                  Custom Message / Vision
                </label>
                <div className="text-sm text-brown/60 mb-4 flex justify-between" data-id="element-447">
                  <span data-id="element-448">
                    Tell us about your vision, special text, or design ideas...
                  </span>
                  <span data-id="element-449">{message.length}/500</span>
                </div>
                <textarea value={message} onChange={e => setMessage(e.target.value.slice(0, 500))} rows={5} className="w-full px-4 py-3 rounded-sm border border-cream-200 focus:border-wine focus:ring-1 focus:ring-wine outline-none transition-colors bg-white resize-none" placeholder="E.g., We'd like a minimalist design with our names and wedding date..." data-id="element-450"></textarea>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Occasions Section */}
      <section className="py-24 bg-white" data-id="element-451">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-452">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-453">
            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal font-bold mb-6" data-id="element-454">
              Perfect For Every{' '}
              <span className="italic text-wine font-normal" data-id="element-455">Occasion</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center" data-id="element-456">
            {occasions.map((occasion, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-cream-50 p-8 rounded-sm border border-cream-200 hover:border-gold/30 transition-colors duration-300 group" data-id="element-457">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300" data-id="element-458">
                  {occasion.icon}
                </div>
                <h3 className="font-playfair text-xl font-bold text-charcoal mb-3" data-id="element-459">
                  {occasion.title}
                </h3>
                <p className="text-brown/70 text-sm leading-relaxed" data-id="element-460">
                  {occasion.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-24 bg-cream-50 overflow-hidden" data-id="element-461">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-462">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-463">
            <span className="text-wine font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-464">
              Inspiration
            </span>
            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal font-bold mb-6" data-id="element-465">
              Recent{' '}
              <span className="italic text-wine font-normal" data-id="element-466">Creations</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-467">
            {galleryImages.map((img, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: idx * 0.1
          }} className="group cursor-pointer" data-id="element-468">
                <div className="relative rounded-sm overflow-hidden shadow-subtle aspect-[3/4] mb-4" data-id="element-469">
                  <img src={img.src} alt={img.caption} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-470" />
                  <div className="absolute inset-0 bg-charcoal/20 group-hover:bg-transparent transition-colors duration-500" data-id="element-471"></div>
                </div>
                <h4 className="font-playfair text-lg font-bold text-charcoal" data-id="element-472">
                  {img.caption}
                </h4>
                <p className="text-brown/60 text-sm mt-1" data-id="element-473">{img.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Quote Request Section */}
      <section id="quote" className="py-24 bg-charcoal text-white" data-id="element-474">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-475">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-476">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-477">
              <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-6" data-id="element-478">
                Request a{' '}
                <span className="italic text-gold font-normal" data-id="element-479">Quote</span>
              </h2>
              <p className="text-cream-200/70 text-lg mb-8 leading-relaxed" data-id="element-480">
                Tell us about your project and we'll get back to you with a
                personalized quote. Pricing depends on wine selection, quantity,
                and label design complexity.
              </p>
              <div className="space-y-4 text-cream-200/60 text-sm" data-id="element-481">
                <div className="flex items-center gap-3" data-id="element-482">
                  <div className="w-1.5 h-1.5 bg-gold rounded-full" data-id="element-483"></div>
                  <span data-id="element-484">Typical response within 24 hours</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-485">
                  <div className="w-1.5 h-1.5 bg-gold rounded-full" data-id="element-486"></div>
                  <span data-id="element-487">No commitment required</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-488">
                  <div className="w-1.5 h-1.5 bg-gold rounded-full" data-id="element-489"></div>
                  <span data-id="element-490">Free design consultation included</span>
                </div>
              </div>
              <div className="mt-10" data-id="element-491">
                <Link to="/" className="text-gold hover:text-white transition-colors text-sm font-medium" data-id="element-492">
                  ← Return Home
                </Link>
              </div>
            </motion.div>

            <motion.form initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm p-8" onSubmit={e => {
            e.preventDefault();
            const form = e.target as HTMLFormElement;
            const name = (form.elements.namedItem('name') as HTMLInputElement).value;
            const email = (form.elements.namedItem('email') as HTMLInputElement).value;
            const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
            const subject = encodeURIComponent(`Custom Label Quote Request from ${name}`);
            const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`);
            window.location.href = `mailto:hello@wineanddesign.com?subject=${subject}&body=${body}`;
          }} data-id="element-493">
              <div className="space-y-5" data-id="element-494">
                <div data-id="element-495">
                  <label htmlFor="name" className="block text-sm font-medium text-cream-200/80 mb-2" data-id="element-496">
                    Full Name
                  </label>
                  <input type="text" id="name" name="name" required placeholder="Your name" className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/30 transition-colors" data-id="element-497" />
                </div>
                <div data-id="element-498">
                  <label htmlFor="email" className="block text-sm font-medium text-cream-200/80 mb-2" data-id="element-499">
                    Email Address
                  </label>
                  <input type="email" id="email" name="email" required placeholder="your@email.com" className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/30 transition-colors" data-id="element-500" />
                </div>
                <div data-id="element-501">
                  <label htmlFor="message" className="block text-sm font-medium text-cream-200/80 mb-2" data-id="element-502">
                    Your Message
                  </label>
                  <textarea id="message" name="message" required rows={5} placeholder="Tell us about your event, quantity needed, wine preferences..." className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/30 transition-colors resize-none" data-id="element-503"></textarea>
                </div>
                <button type="submit" className="w-full py-3.5 bg-gold text-charcoal font-medium rounded-sm hover:bg-white transition-colors duration-300" data-id="element-504">
                  Send Quote Request
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </section>
    </main>;
}